import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import {
  approveRequisition,
  fetchApprovalQueue,
  fetchBudgetPeriods,
  fetchCostCenters,
  fetchRequisitionStates,
  rejectRequisition,
  returnRequisition,
  IApprovalQueueFilters,
  IApprovalQueueItem,
} from './forms.apis';
import { ILookupItem } from './forms.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  acting: boolean;
  error: string;
  success: string;

  rows: IApprovalQueueItem[];
  total: number;

  filters: IApprovalQueueFilters;
  periods: ILookupItem[];
  costcenters: ILookupItem[];
  states: ILookupItem[];

  actionDialogOpen: boolean;
  actionType: 'approve' | 'return' | 'reject' | '';
  actionTarget: IApprovalQueueItem | null;
  actionRemarks: string;
};

const DEFAULT_FILTERS: IApprovalQueueFilters = {
  search: '',
  periodix: '',
  costcenterix: '',
  stateix: '',
  page: 1,
  pagesize: 20,
};

export default class ApprovalQueuePage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      acting: false,
      error: '',
      success: '',

      rows: [],
      total: 0,

      filters: { ...DEFAULT_FILTERS },
      periods: [],
      costcenters: [],
      states: [],

      actionDialogOpen: false,
      actionType: '',
      actionTarget: null,
      actionRemarks: '',
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [periods, costcenters, states] = await Promise.all([
        fetchBudgetPeriods(),
        fetchCostCenters(),
        fetchRequisitionStates(),
      ]);

      this.setState(
        {
          periods,
          costcenters,
          states,
        },
        () => this.loadRows()
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load approval queue lookups',
      });
    }
  };

  private loadRows = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const result = await fetchApprovalQueue(this.state.filters);

      this.setState({
        rows: result.items || [],
        total: result.total || 0,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load approvals queue',
      });
    }
  };

  private handleTextChange =
    (field: keyof IApprovalQueueFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IApprovalQueueFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadRows();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
      },
      () => this.loadRows()
    );
  };

  private handlePaginationChange = (model: GridPaginationModel): void => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          page: model.page + 1,
          pagesize: model.pageSize,
        },
      }),
      () => this.loadRows()
    );
  };

  // private handleOpen = (row: IApprovalQueueItem): void => {
  //   const path = `/forms/requisitions/${row.recix}`;

  //   if (this.props.navigate) {
  //     this.props.navigate(path);
  //     return;
  //   }

  //   window.location.href = path;
  // };

  private handleOpen = (row: IApprovalQueueItem): void => {
    if (this.props.navigate) {
      this.props.navigate('detail',row);
      return;
    }
  };

  private openActionDialog = (
    actionType: 'approve' | 'return' | 'reject',
    row: IApprovalQueueItem
  ): void => {
    this.setState({
      actionDialogOpen: true,
      actionType,
      actionTarget: row,
      actionRemarks: '',
      error: '',
      success: '',
    });
  };

  private closeActionDialog = (): void => {
    this.setState({
      actionDialogOpen: false,
      actionType: '',
      actionTarget: null,
      actionRemarks: '',
    });
  };

  private handleActionRemarksChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      actionRemarks: event.target.value,
    });
  };

  private handleConfirmAction = async (): Promise<void> => {
    const { actionType, actionTarget, actionRemarks } = this.state;

    if (!actionType || !actionTarget) {
      return;
    }

    this.setState({
      acting: true,
      error: '',
      success: '',
    });

    try {
      if (actionType === 'approve') {
        await approveRequisition(actionTarget.recix, { remarks: actionRemarks });
      } else if (actionType === 'return') {
        await returnRequisition(actionTarget.recix, { remarks: actionRemarks });
      } else if (actionType === 'reject') {
        await rejectRequisition(actionTarget.recix, { remarks: actionRemarks });
      }

      this.setState({
        acting: false,
        actionDialogOpen: false,
        success: `Requisition ${actionType}d successfully.`,
        actionType: '',
        actionTarget: null,
        actionRemarks: '',
      });

      this.loadRows();
    } catch (error: any) {
      this.setState({
        acting: false,
        error: error?.message || `Failed to ${actionType} requisition`,
      });
    }
  };

  private getColumns = (): GridColDef<IApprovalQueueItem>[] => {
    return [
      {
        field: 'reference',
        headerName: 'Reference',
        minWidth: 150,
        flex: 0.8,
      },
      {
        field: 'recname',
        headerName: 'Title',
        minWidth: 240,
        flex: 1.4,
      },
      {
        field: 'costcenter',
        headerName: 'Cost Center',
        minWidth: 150,
        flex: 0.9,
        valueGetter: (_value, row) => row.costcenter?.recname || '',
      },
      {
        field: 'period',
        headerName: 'Budget Period',
        minWidth: 130,
        flex: 0.8,
        valueGetter: (_value, row) => row.period?.recname || '',
      },
      {
        field: 'requesteddate',
        headerName: 'Requested Date',
        minWidth: 130,
        flex: 0.8,
        valueGetter: (_value, row) =>
          row.requesteddate ? moment(row.requesteddate).format('YYYY-MM-DD') : '',
      },
      {
        field: 'totalestimate',
        headerName: 'Requested Total',
        minWidth: 140,
        flex: 0.8,
        type: 'number',
        valueFormatter: (value: any) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'reviewedtotal',
        headerName: 'Reviewed Total',
        minWidth: 140,
        flex: 0.8,
        type: 'number',
        valueFormatter: (value: any) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'itemcount',
        headerName: 'Items',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 320,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<IApprovalQueueItem>) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => this.handleOpen(params.row)}
            >
              Open
            </Button>
            <Button
              size="small"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => this.openActionDialog('approve', params.row)}
            >
              Approve
            </Button>
            <Button
              size="small"
              color="warning"
              startIcon={<UndoIcon />}
              onClick={() => this.openActionDialog('return', params.row)}
            >
              Return
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => this.openActionDialog('reject', params.row)}
            >
              Reject
            </Button>
          </Stack>
        ),
      },
    ];
  };

  private getActionTitle = (): string => {
    const { actionType } = this.state;

    if (actionType === 'approve') return 'Approve Requisition';
    if (actionType === 'return') return 'Return Requisition';
    if (actionType === 'reject') return 'Reject Requisition';
    return 'Action';
  };

  render(): React.ReactNode {
    const {
      loading,
      acting,
      error,
      success,
      rows,
      total,
      filters,
      periods,
      costcenters,
      states,
      actionDialogOpen,
      actionTarget,
      actionRemarks,
    } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Approvals Queue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Requisitions ready for approval, return, or rejection.
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={this.handleTextChange('search')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="approval-period-label">Budget Period</InputLabel>
                  <Select
                    labelId="approval-period-label"
                    value={filters.periodix}
                    label="Budget Period"
                    onChange={this.handleSelectChange('periodix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {periods.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="approval-cc-label">Cost Center</InputLabel>
                  <Select
                    labelId="approval-cc-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="approval-state-label">State</InputLabel>
                  <Select
                    labelId="approval-state-label"
                    value={filters.stateix}
                    label="State"
                    onChange={this.handleSelectChange('stateix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {states.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: 640, width: '100%' }}>
              {loading && rows.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%' }}
                  spacing={2}
                >
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Loading approvals queue...
                  </Typography>
                </Stack>
              ) : (
                <DataGrid
                  rows={rows}
                  columns={this.getColumns()}
                  getRowId={(row) => row.recix}
                  rowCount={total}
                  loading={loading}
                  paginationMode="server"
                  pageSizeOptions={[10, 20, 50, 100]}
                  paginationModel={{
                    page: filters.page - 1,
                    pageSize: filters.pagesize,
                  }}
                  onPaginationModelChange={this.handlePaginationChange}
                  disableRowSelectionOnClick
                  sx={{ border: 0 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        <Dialog
          open={actionDialogOpen}
          onClose={this.closeActionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{this.getActionTitle()}</DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2">
                <strong>Reference:</strong> {actionTarget?.reference || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Title:</strong> {actionTarget?.recname || '-'}
              </Typography>

              <TextField
                fullWidth
                label="Remarks"
                multiline
                minRows={4}
                value={actionRemarks}
                onChange={this.handleActionRemarksChange}
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeActionDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={this.handleConfirmAction}
              disabled={acting}
            >
              {acting ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}